import { useTranslation } from 'react-i18next';
import type { ReplitProcedure } from '@/types/replit-dashboard';
import ExplainButton from './ExplainButton';
import { formatRecordDate } from './records-utils';

interface ProceduresListProps {
  procedures: ReplitProcedure[];
  patientFirstName: string;
  onAskQuestion: (question: string) => void;
}

export default function ProceduresList({ procedures, patientFirstName, onAskQuestion }: ProceduresListProps) {
  const { t, i18n } = useTranslation();

  if (!procedures || procedures.length === 0) {
    return <p className="text-sm text-gray-500">{t('dashboard.noProceduresRecorded')}</p>;
  }

  return (
    <div className="space-y-3 text-sm">
      {procedures.slice(0, 5).map((procedure, index) => {
        const performedDate = formatRecordDate(procedure.performedDate, i18n.resolvedLanguage || 'en');
        return (
          <div key={procedure.id || index} className="border-l-4 border-blue-500 p-3 bg-blue-50 rounded-r-lg">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-blue-900 text-sm">{procedure.procedure}</h4>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                procedure.status === 'completed' ? 'bg-green-200 text-green-800' :
                procedure.status === 'in-progress' ? 'bg-yellow-200 text-yellow-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {procedure.status}
              </span>
            </div>
            {performedDate && (
              <div className="text-xs text-blue-800 mb-1">
                <strong>{t('dashboard.procedureDateLabel')}</strong> {performedDate}
              </div>
            )}
            {procedure.performer && (
              <div className="text-xs text-blue-800 mb-1.5">
                <strong>{t('dashboard.performedByLabel')}</strong> {procedure.performer}
              </div>
            )}
            <div className="flex justify-end">
              <ExplainButton
                onClick={() => onAskQuestion(t('explain.procedureDetail', { procedure: procedure.procedure, name: patientFirstName }))}
                ariaLabel={t('dashboard.explainProcedureAriaLabel', { procedure: procedure.procedure })}
                testId={`button-explain-procedure-${index}`}
              />
            </div>
          </div>
        );
      })}
      {procedures.length > 5 && (
        <p className="text-xs text-gray-500">{t('dashboard.moreProcedures', { count: procedures.length - 5 })}</p>
      )}
    </div>
  );
}
